import React, { useState, useEffect } from 'react';
import { Input, Button, message, InputNumber, Collapse } from 'antd';
import { PlusOutlined, DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import CustomModal from '@/components/UI/Modals/CustomModal';
import { useAddCateringCategoryProductsMutation, useGetCateringCategoryProductsByCatIdQuery } from '@/services/api/event/cateringCategoryApi';
import "./eventModal.css"
/**
 * Add Products Modal Component
 * Handles adding categories and products to a package
 */
const AddProductsModal = ({ 
  visible, 
  onCancel, 
  onSuccess,
  packageData 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  
  // API hooks
  const [addCateringCategoryProducts, { isLoading: isAddingProducts }] = useAddCateringCategoryProductsMutation();
  
  // Fetch existing products for this package
  const { 
    data: existingProducts, 
    isLoading: isLoadingExisting,
    error: existingProductsError 
  } = useGetCateringCategoryProductsByCatIdQuery(packageData?.id, {
    skip: !packageData?.id || !visible
  });
  
  // Side modal states
  const [isAddProductsModalOpen, setIsAddProductsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [newProductName, setNewProductName] = useState('');
  const [newProductPrice, setNewProductPrice] = useState(0);

  // Load existing products when modal opens
  useEffect(() => {
    if (visible && existingProducts?.catering_categories) {
      // Transform existing products to match our local state format
      const transformedCategories = existingProducts.catering_categories.map(category => ({
        id: category.id,
        name: category.name,
        products: category.catering_products?.map(product => ({
          id: product.id,
          name: product.name,
          pricePerPlate: parseFloat(product.price)
        })) || []
      }));
      setCategories(transformedCategories);
    } else if (visible && !isLoadingExisting) {
      // Reset categories if no existing products
      setCategories([]);
    }
  }, [visible, existingProducts, isLoadingExisting]);

  // Handle adding a new category
  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      message.error('Please enter a category name');
      return;
    }

    const newCategory = {
      id: Date.now(), // Simple ID generation
      name: newCategoryName.trim(),
      products: []
    };

    setCategories([...categories, newCategory]);
    setNewCategoryName('');
    setShowAddCategory(false);
    message.success('Menu section added successfully');
  };

  // Handle opening add products modal for a category
  const handleAddProductsToCategory = (category) => {
    setSelectedCategory(category);
    setProducts(category.products || []);
    setIsAddProductsModalOpen(true);
  };

  // Handle adding a new product in the side modal
  const handleAddProduct = () => {
    if (!newProductName.trim()) {
      message.error('Please enter a product name');
      return;
    }

    if (newProductPrice <= 0) {
      message.error('Please enter a valid price');
      return;
    }

    const newProduct = {
      id: Date.now(),
      name: newProductName.trim(),
      pricePerPlate: newProductPrice
    };

    setProducts([...products, newProduct]);
    setNewProductName('');
    setNewProductPrice(0);
    message.success('Product added successfully');
  };

  // Handle removing a product in the side modal
  const handleRemoveProduct = (productId) => {
    setProducts(products.filter(product => product.id !== productId));
    message.success('Product removed');
  };

  // Handle saving products back to the category
  const handleSaveProducts = () => {
    setCategories(categories.map(category => 
      category.id === selectedCategory.id 
        ? { ...category, products: products }
        : category
    ));
    setIsAddProductsModalOpen(false);
    setSelectedCategory(null);
    setProducts([]);
    message.success('Products saved to menu section');
  };

  // Handle removing a category
  const handleRemoveCategory = (categoryId) => {
    setCategories(categories.filter(category => category.id !== categoryId));
    message.success('Menu section removed');
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate that all products have names and prices
    const hasEmptyProducts = categories.some(category => 
      category.products.some(product => !product.name.trim() || product.pricePerPlate <= 0)
    );

    if (hasEmptyProducts) {
      message.error('Please fill in all product names and prices');
      return;
    }

    if (categories.length === 0) {
      message.error('Please add at least one category');
      return;
    }

    setIsSubmitting(true);
    try {
      // Transform categories to the required API payload format
      const payload = {
        categories: categories.map(category => ({
          catering_package_id: packageData.id,
          name: category.name,
          catering_products: category.products.map(product => ({
            name: product.name,
            price: product.pricePerPlate,
            product_type: 2 // Default product type as specified in the example
          }))
        }))
      };

      // console.log('Submitting products for package:', packageData?.name);
      // console.log('API Payload:', payload);
      
      // Call the API endpoint
      await addCateringCategoryProducts(payload).unwrap();
      
      message.success('Products added successfully!');
      onSuccess && onSuccess();
      handleCancel();
    } catch (error) {
      console.error('Error adding products:', error);
      message.error(error?.data?.message || 'Failed to add products');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleCancel = () => {
    setCategories([]);
    setNewCategoryName('');
    setShowAddCategory(false);
    setIsAddProductsModalOpen(false);
    setSelectedCategory(null);
    setProducts([]);
    setNewProductName('');
    setNewProductPrice(0);
    onCancel();
  };

  // Handle closing side modal
  const handleCloseSideModal = () => {
    setIsAddProductsModalOpen(false);
    setSelectedCategory(null);
    setProducts([]);
    setNewProductName('');
    setNewProductPrice(0);
  };

  if (!packageData) return null;

  return (
    <>
      {/* Main Modal */}
      <CustomModal
        isOpen={visible}
        onClose={handleCancel}
        title={packageData.name}
        subtitle={categories.length > 0 ? "Edit products for the above package" : "Add Products to the above package"}
        width={600}
        rightOffset={isAddProductsModalOpen ? -80 : 1}
        primaryActionText={isSubmitting || isAddingProducts ? 'Saving Products...' : (categories.length > 0 ? 'Update Package Products' : 'Add Products to Package')}
        onPrimaryAction={handleSubmit}
        primaryActionDisabled={categories.length === 0}
        loading={isSubmitting || isAddingProducts}
        hideSecondaryAction={categories.length === 0}
        secondaryActionText="Cancel"
        onSecondaryAction={handleCancel}
      >
        <div className="p-6">
          {isLoadingExisting ? (
            /* Loading State */
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⏳</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Loading existing products...</h3>
              <p className="text-gray-500">Please wait while we fetch the current package products</p>
            </div>
          ) : categories.length === 0 && !showAddCategory ? (
            /* Empty State */
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Menu Section Added</h3>
              <p className="text-gray-500 mb-6">Start by adding a menu section to organize your products</p>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowAddCategory(true)}
                className="add-cat-products"
              >
                Add Menu Section
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Categories List - Accordion when multiple, simple when single */}
              {categories.length === 1 ? (
                // Single category - show as simple card
                categories.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-medium">✓</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
                          <p className="text-xs text-gray-500">
                            {category.products?.length || 0} {category.products?.length === 1 ? 'product' : 'products'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleAddProductsToCategory(category)}
                          className="add-products-to-menu"
                        >
                          + Add Products
                        </Button>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveCategory(category.id)}
                          className="text-red-500 hover:text-red-700"
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Multiple categories - show as accordion
                <Collapse
                  ghost
                  expandIcon={({ isActive }) => isActive ? <UpOutlined /> : <DownOutlined />}
                  expandIconPosition="end"
                  items={categories.map((category) => ({
                    key: category.id,
                    label: (
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-medium">✓</span>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
                            <p className="text-xs text-gray-500">
                              {category.products?.length || 0} {category.products?.length === 1 ? 'product' : 'products'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddProductsToCategory(category);
                            }}
                            className="bg-blue-500 border-blue-500 hover:bg-blue-600"
                          >
                            + Add Products
                          </Button>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCategory(category.id);
                            }}
                            className="text-red-500 hover:text-red-700"
                          />
                        </div>
                      </div>
                    ),
                    children: (
                      <div className="pt-2">
                        {category.products && category.products.length > 0 ? (
                          <div className="space-y-2">
                            {category.products.map((product) => (
                              <div key={product.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-gray-900">{product.name}</span>
                                  <span className="text-gray-900 font-semibold">
                                    ₦{product.pricePerPlate?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            No products added yet. Click "Add Products" to get started.
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                />
              )}

              {/* Simple Add Category */}
              {showAddCategory ? (
                <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter menu section name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onPressEnter={handleAddCategory}
                      className="flex-1 h-10"
                    />
                    <Button
                      type="primary"
                      onClick={handleAddCategory}
                      className="bg-orange-500 border-orange-500 hover:bg-orange-600 h-10 px-6"
                    >
                      Add
                    </Button>
                    <Button
                      onClick={() => setShowAddCategory(false)}
                      className="h-10 px-4"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddCategory(true)}
                  className="w-full h-10 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-500"
                >
                  + Add Category
                </Button>
              )}
            </div>
          )}
        </div>
      </CustomModal>

      {/* Side Modal for Adding Products */}
      <CustomModal
        isOpen={isAddProductsModalOpen}
        onClose={handleCloseSideModal}
        title={`Add Products to ${selectedCategory?.name}`}
        subtitle="Add multiple products to this menu section"
        width={700}
        rightOffset={230}
        primaryActionText="Save Products"
        onPrimaryAction={handleSaveProducts}
        secondaryActionText="Cancel"
        onSecondaryAction={handleCloseSideModal}
        primaryButtonColor="grey"
      >
        <div className="p-6">
          {/* Sleek Add Product Form */}
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 mb-6 border border-orange-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                <PlusOutlined className="text-white text-xs" />
              </div>
              <h4 className="text-base font-medium text-gray-900">Add New Product</h4>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name
                </label>
                <Input
                  placeholder="e.g. Grilled Chicken Breast"
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  className="h-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price per Plate
                </label>
                <InputNumber
                  placeholder="0.00"
                  value={newProductPrice}
                  onChange={(value) => setNewProductPrice(value || 0)}
                  min={0}
                  step={0.01}
                  className="w-full h-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₦\s?|(,*)/g, '')}
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddProduct}
                className="add-products-to-cat"
              >
                Add to Category
              </Button>
            </div>
          </div>

          {/* Enhanced Products List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-medium text-gray-900">Products in <b>"{selectedCategory?.name}"</b> section</h4>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {products.length} {products.length === 1 ? 'product' : 'products'}
              </span>
            </div>
            
            {products.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <div className="text-3xl mb-2">🍽️</div>
                <h5 className="text-base font-medium text-gray-900 mb-1">No products yet</h5>
                <p className="text-sm text-gray-500">Add your first product using the form above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {products.map((product, index) => (
                  <div key={product.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-medium text-xs">{index + 1}</span>
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900 text-base">{product.name}</h5>
                          <p className="text-gray-900 font-semibold text-base">
                            ₦{product.pricePerPlate?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveProduct(product.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg p-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default AddProductsModal;
